import { Component, Input, OnInit, DoCheck, KeyValueDiffers, KeyValueDiffer } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { isNilty } from '@core/utils.service';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-compute-metadata',
  templateUrl: './compute-metadata.component.html',
  styleUrls: ['./compute-metadata.component.css'],
})
export class ComputeMetadataComponent implements OnInit, DoCheck {
  @Input() name: string;
  parts: any[] = [];
  newTextValue = '';
  selectedMetadata: string = null;
  availableKeysTitle: string[];
  mapTitle: any = {};
  allCustomMetadataFields: string[];
  allowedFields: string[];
  availableKeys: string[];
  filteredAvailableKeys: Observable<string[]>;
  availableKeysControl = new FormControl<string>('');
  selectedTitleKey: string;
  existingKeys: string[] = [];

  private metadataPositionsDiffer: KeyValueDiffer<string, any>;
  private metadataPositionsRef: any;

  private positionsDiffer: KeyValueDiffer<string, any>;
  private positionsRef: any;

  constructor(private differs: KeyValueDiffers) {}

  @Input() set _positions(p: any) {
    this.positionsRef = p;
    if (p && typeof p === 'object' && !Array.isArray(p)) {
      if (!this.positionsDiffer) {
        try {
          this.positionsDiffer = this.differs.find(p).create();
        } catch (e) {
          console.warn('Cannot create differ for positions', e);
        }
      }
    } else {
      this.positionsDiffer = null;
    }
    this.updateAllowedFields();
  }

  @Input() set _metadataPositions(mp: any) {
    this.metadataPositionsRef = mp;
    if (mp && typeof mp === 'object' && !Array.isArray(mp)) {
      if (!this.metadataPositionsDiffer) {
        try {
          this.metadataPositionsDiffer = this.differs.find(mp).create();
        } catch (e) {
          console.warn('Cannot create differ for metadataPositions', e);
        }
      }
    } else {
      this.metadataPositionsDiffer = null;
    }
    this.updateAllowedFields();
  }

  @Input() set _allCustomMetadataFields(cmf: string[]) {
    this.allCustomMetadataFields = cmf || [];
    this.availableKeys = [...this.allCustomMetadataFields];
    this.updateAllowedFields();
  }

  @Input() set _mapTitle(mt: any) {
    this.mapTitle = mt || {};
    this.existingKeys = Object.keys(this.mapTitle);
  }

  ngDoCheck(): void {
    let changed = false;
    if (this.metadataPositionsDiffer && this.metadataPositionsRef) {
      const changes = this.metadataPositionsDiffer.diff(this.metadataPositionsRef);
      if (changes) {
        changed = true;
      }
    }
    if (this.positionsDiffer && this.positionsRef) {
      const changes = this.positionsDiffer.diff(this.positionsRef);
      if (changes) {
        changed = true;
      }
    }
    if (changed) {
      this.updateAllowedFields();
    }
  }

  updateAllowedFields() {
    const metadataKeys =
      this.metadataPositionsRef && typeof this.metadataPositionsRef === 'object' && !Array.isArray(this.metadataPositionsRef)
        ? Object.keys(this.metadataPositionsRef)
        : this.metadataPositionsRef || [];

    const positionKeys =
      this.positionsRef && typeof this.positionsRef === 'object' && !Array.isArray(this.positionsRef)
        ? Object.keys(this.positionsRef)
        : this.positionsRef || [];

    // allowedFields = union of metadataPositions keys + positions keys (available for "Add Metadata")
    // Exclude 'TITLE' from positions since it's used as a title key, not a metadata field
    const filteredPositionKeys = positionKeys.filter((k: string) => k.toUpperCase() !== 'TITLE');
    this.allowedFields = [...new Set([...metadataKeys, ...filteredPositionKeys])];

    this.calculateAvailableKeysTitle();
  }

  calculateAvailableKeysTitle() {
    if (this.availableKeys) {
      // availableKeysTitle are keys NOT in allowedFields (used for the title key selector)
      const baseKeys = this.availableKeys.filter((it) => !this.allowedFields.includes(it));

      // Add 'TITLE' as a selectable key only if it is NOT already present in positions
      const titleExistsInPositions =
        this.positionsRef && typeof this.positionsRef === 'object' && !Array.isArray(this.positionsRef)
          ? Object.keys(this.positionsRef).some((k) => k.toUpperCase() === 'TITLE')
          : false;

      if (!titleExistsInPositions && !baseKeys.includes('TITLE')) {
        this.availableKeysTitle = [...baseKeys, 'TITLE'];
      } else {
        this.availableKeysTitle = [...baseKeys];
      }
    }
  }

  ngOnInit(): void {
    // Autocomplete filter logic
    this.filteredAvailableKeys = this.availableKeysControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const name = typeof value === 'string' ? value : '';
        if (isNilty(this.availableKeys)) {
          return [];
        }
        return name ? this._filter(name as string) : this.availableKeys.slice();
      })
    );
  }

  _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.availableKeys.filter((option) => option.toLowerCase().includes(filterValue));
  }

  // Called when user selects a Title Key (e.g. TITLE_EBAY)
  onTitleKeyChange() {
    if (this.existingKeys.includes(this.selectedTitleKey)) {
      // If the user selects a key that is already saved, we "edit" it by moving it from the saved list to the edit area
      this.existingKeys = this.existingKeys.filter((k) => k !== this.selectedTitleKey);
    }

    if (this.selectedTitleKey && this.mapTitle[this.selectedTitleKey]) {
      this.parts = this.parseStringIntoParts(this.mapTitle[this.selectedTitleKey]);
    } else {
      this.parts = [];
    }
  }

  addMetadata() {
    if (this.selectedMetadata) {
      this.parts.push({ t: 'm', v: this.selectedMetadata });
      this.selectedMetadata = '';
      this.updateFinalString();
    }
  }

  addText() {
    if (this.newTextValue) {
      this.parts.push({ t: 's', v: this.newTextValue });
      this.newTextValue = '';
      this.updateFinalString();
    }
  }

  removePart(index: number) {
    this.parts.splice(index, 1);
    this.updateFinalString();
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.parts, event.previousIndex, event.currentIndex);
    this.updateFinalString();
  }

  saveTitle() {
    if (this.selectedTitleKey) {
      if (!this.existingKeys.includes(this.selectedTitleKey)) {
        this.existingKeys.push(this.selectedTitleKey);
      }
    }

    // The map is already updated real-time, so we just clear the selection to allow picking a new one
    this.selectedTitleKey = null;
    this.parts = [];
    this.selectedMetadata = null;
    this.newTextValue = '';
  }

  editTitle(key: string) {
    this.selectedTitleKey = key;
    this.onTitleKeyChange();
  }

  deleteTitle(key: string) {
    if (this.mapTitle[key]) {
      delete this.mapTitle[key];
    }
    this.existingKeys = this.existingKeys.filter((k) => k !== key);

    // If we are currently editing the deleted key, clear the form
    if (this.selectedTitleKey === key) {
      this.selectedTitleKey = null;
      this.parts = [];
      this.selectedMetadata = null;
      this.newTextValue = '';
    }
  }

  updateFinalString() {
    const final = this.parts
      .map((p) => {
        return p.t === 'm' ? `[${p.v}]` : p.v;
      })
      .join(' ');

    if (this.selectedTitleKey) {
      this.mapTitle[this.selectedTitleKey] = final;
    }
  }

  parseStringIntoParts(val: string) {
    if (!val) return [];
    const parts = [];
    const regex = /\[(.*?)]/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(val)) !== null) {
      // Text before the valid match
      if (match.index > lastIndex) {
        parts.push({ t: 's', v: val.substring(lastIndex, match.index) });
      }
      // The metadata itself (match[1] is the content inside [])
      parts.push({ t: 'm', v: match[1] });
      lastIndex = regex.lastIndex;
    }

    // Remaining text
    if (lastIndex < val.length) {
      parts.push({ t: 's', v: val.substring(lastIndex) });
    }
    return parts;
  }
}
